import { ref, unref, computed, defineComponent, toRefs, watch, resolveComponent, openBlock, createBlock, withCtx, createElementVNode, mergeProps, createElementBlock, normalizeClass, createCommentVNode, renderSlot } from 'vue';
import { usePointerSwipe, onClickOutside } from '@vueuse/core';
import { u as ue, t as te } from '../OrAutocompleteV3-8c6de221.js';
import { OrOverlayV3 as script$1 } from '../or-overlay-v3/index.js';
import '@onereach/styles/screens.json';
import '@onereach/styles/tailwind.config.json';
import 'lodash/get';
import 'lodash/isElement';
import '../or-error-v3/index.js';
import '../or-hint-v3/index.js';
import '../or-icon-button-v3/index.js';
import '../or-icon-v3/index.js';
import '../or-tooltip-v3/index.js';
import '../or-popover-v3/index.js';
import '@floating-ui/dom';
import './index.js';
import '../or-teleport-v3/index.js';
import '../or-input-box-v3/index.js';
import '../or-input-v3/index.js';
import '../OrInlineInputV3-061441a3.js';
import '../or-inline-input-box-v3/index.js';
import '../or-text-v3/index.js';
import '../or-label-v3/index.js';
import '../or-loader-v3/index.js';
import '../or-menu-item-v3/index.js';
import '../or-tag-input-v3/index.js';
import '../or-tags-v3/index.js';
import '../or-button-v3/index.js';
import '../or-tag-v3/index.js';

var BottomSheetVariant;
(function (BottomSheetVariant) {
  BottomSheetVariant["Basic"] = "basic";
  BottomSheetVariant["Advanced"] = "advanced";
})(BottomSheetVariant || (BottomSheetVariant = {}));

function useBottomSheetSwipeState(root, content, fullscreen, variant, close) {
  const inlineSwipingStyles = ref({});
  const skipSwipe = ref(false);
  const theme = ue();
  const isEventSkipped = event => {
    return variant.value === BottomSheetVariant.Basic || event.composedPath().includes(content.value) && fullscreen.value;
  };
  const {
    isSwiping
  } = usePointerSwipe(root, {
    threshold: 12,
    onSwipe(event) {
      skipSwipe.value = isEventSkipped(event);
      if (variant.value === BottomSheetVariant.Advanced) {
        const rootElement = unref(root);
        inlineSwipingStyles.value = {
          transitionProperty: 'none',
          minHeight: `${rootElement.clientHeight - event.offsetY}px`,
          maxHeight: `${rootElement.clientHeight - event.offsetY}px`
        };
      }
    },
    onSwipeEnd(event, direction) {
      if (isEventSkipped(event)) {
        return;
      }
      if (variant.value === BottomSheetVariant.Advanced) {
        switch (direction) {
          case 'up':
          case 'UP':
            fullscreen.value = true;
            break;
          case 'down':
          case 'DOWN':
            if (fullscreen.value) {
              fullscreen.value = false;
            } else {
              close();
            }
            break;
        }
      }
    }
  });
  const rootInlineStyles = computed(() => {
    if (variant.value === BottomSheetVariant.Basic) {
      return {
        transitionProperty: 'min-height, max-height',
        minHeight: '0',
        maxHeight: `calc(100% - ${theme.spacing.xl})`
      };
    }
    return isSwiping.value && !skipSwipe.value ? inlineSwipingStyles.value : fullscreen.value ? {
      transitionProperty: 'min-height, max-height',
      minHeight: `calc(100% - ${theme.spacing.xl})`,
      maxHeight: `calc(100% - ${theme.spacing.xl})`
    } : {
      transitionProperty: 'min-height, max-height',
      minHeight: '0',
      maxHeight: '50%'
    };
  });
  return {
    rootInlineStyles
  };
}

const BottomSheet = [
// Position
'fixed', 'left-0', 'right-0', 'bottom-0',
// Layout
'layout-column',
// Shape
'rounded-t-lg',
// Elevation
'shadow-contextual', 'dark:shadow-contextual-dark',
// Typography
'typography-body-2-regular',
// Theme
'theme-background-default', 'dark:theme-background-default-dark', 'theme-foreground-default', 'dark:theme-foreground-default-dark'];
const BottomSheetVariants = {
  [BottomSheetVariant.Basic]: [],
  [BottomSheetVariant.Advanced]: [
  // Box
  'max-h-[50%]',
  // Transition
  'transition-[min-height]', 'transition-[max-height]']
};
const BottomSheetHandle = [
// Layout
'layout-row justify-center',
// Interactivity
'interactivity-drag',
// Box
'after:w-2xl', 'after:h-xs',
// Spacing
'px-sm', 'py-sm',
// Shape
'after:rounded-full',
// Theme
'after:bg-outline', 'after:dark:bg-outline'];
const BottomSheetHeader = [
// Layout (empty)
'empty:hidden',
// Spacing
'px-lg', 'py-md',
// Typography
'typography-headline-3'];
const BottomSheetContent = [
// Layout
'layout-column', 'grow'];

var script = defineComponent({
  name: 'OrBottomSheet',
  components: {
    OrOverlay: script$1
  },
  inheritAttrs: false,
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: () => BottomSheetVariant.Advanced
    },
    isFullscreen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:state', 'open', 'close'],
  expose: ['root', 'state', 'fullscreen', 'open', 'close', 'toggle'],
  setup(props, context) {
    // Refs
    const root = ref();
    const content = ref();
    // Props
    const {
      variant,
      isOpen
    } = toRefs(props);
    // State
    const {
      state,
      open,
      close,
      toggle
    } = te(isOpen, context.emit);
    const fullscreen = ref(props.isFullscreen);
    // Styles
    const rootStyles = computed(() => ['or-bottom-sheet-v3', ...BottomSheet, ...BottomSheetVariants[props.variant]]);
    const handleStyles = computed(() => [...BottomSheetHandle]);
    const headerStyles = computed(() => [...BottomSheetHeader]);
    const contentStyles = computed(() => [...BottomSheetContent, fullscreen.value || variant.value === 'basic' ? 'overflow-y-auto' : 'overflow-hidden']);
    // Effects
    // #region Swipe
    const {
      rootInlineStyles
    } = useBottomSheetSwipeState(root, content, fullscreen, variant, close);
    // #endregion
    watch(state, value => {
      if (value === 'closed') {
        fullscreen.value = props.isFullscreen;
      }
    });
    // #region Click Outside
    onClickOutside(root, event => {
      const path = event.composedPath();
      if (!path.some(({
        classList
      }) => (classList === null || classList === void 0 ? void 0 : classList.contains('or-popover-v3')) || (classList === null || classList === void 0 ? void 0 : classList.contains('or-bottom-sheet-v3')))) {
        close();
      }
    });
    // #endregion
    return {
      root,
      content,
      rootStyles,
      rootInlineStyles,
      handleStyles,
      headerStyles,
      contentStyles,
      state,
      fullscreen,
      open,
      close,
      toggle
    };
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_OrOverlay = resolveComponent("OrOverlay");
  return _ctx.state === 'open' ? (openBlock(), createBlock(_component_OrOverlay, {
    key: 0,
    fullscreen: _ctx.fullscreen
  }, {
    default: withCtx(() => [createElementVNode("div", mergeProps(_ctx.$attrs, {
      ref: 'root',
      class: _ctx.rootStyles,
      style: _ctx.rootInlineStyles
    }), [_ctx.variant === 'advanced' ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: normalizeClass(_ctx.handleStyles)
    }, null, 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
      class: normalizeClass(_ctx.headerStyles)
    }, [renderSlot(_ctx.$slots, "header")], 2 /* CLASS */), createElementVNode("div", {
      ref: 'content',
      class: normalizeClass(_ctx.contentStyles)
    }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */)], 16 /* FULL_PROPS */)]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["fullscreen"])) : createCommentVNode("v-if", true);
}

script.render = render;
script.__file = "src/components/or-bottom-sheet-v3/OrBottomSheet.vue";

export { BottomSheetVariant, script as OrBottomSheetV3 };
